import { useRef, useState } from "react";
import OpenAI from "openai";
import { useApiKey } from "../ApiKeyContext";
import "../App.css";
import "./STCMJSC.css";
import GameRulesCard from "./GameRulesCard";
import "./GameRulesCard.css";
import stcmjscLogo from "../assets/logo-stcmjsc.png";

type STCMJSCProps = { onQuit: () => void };

export default function STCMJSC({ onQuit }: STCMJSCProps) {
  const apiKey = useApiKey();
  const openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
  const assistantId = "asst_ZZIWTd11GgTS95jefjKfGiQR";
  const threadId = useRef<string | null>(null);

  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [playerName, setPlayerName] = useState("");
  const [players, setPlayers] = useState<string[]>([]);
  const [questionIndex, setQuestionIndex] = useState(1);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const ensureThread = async () => {
    if (!threadId.current) {
      const t = await openai.beta.threads.create();
      threadId.current = t.id;
    }
    return threadId.current!;
  };

  const startGame = async () => {
    setLoading(true);
    try {
      const tid = await ensureThread();
      const intro = `Les joueurs sont : ${players.join(", ")}.`;
      await openai.beta.threads.messages.create(tid, {
        role: "user",
        content: intro,
      });
      const run = await openai.beta.threads.runs.createAndPoll(tid, {
        assistant_id: assistantId,
      });
      const msgs = await openai.beta.threads.messages.list(tid);
      const msg = msgs.data.find(
        (m) => m.run_id === run.id && m.role === "assistant"
      );
      const block = msg?.content?.find((b) => b.type === "text");
      setCurrentPrompt(
        block && "text" in block ? block.text.value : "Erreur lors de la génération"
      );
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = async () => {
    setLoading(true);
    try {
      const tid = await ensureThread();
      const prompt = `Génére une nouvelle question Picolo drôle pour les joueurs : ${players.join(
        ", "
      )}. C’est la question ${questionIndex + 1} sur 30.`;
      await openai.beta.threads.messages.create(tid, {
        role: "user",
        content: prompt,
      });
      const run = await openai.beta.threads.runs.createAndPoll(tid, {
        assistant_id: assistantId,
      });
      const msgs = await openai.beta.threads.messages.list(tid);
      const msg = msgs.data.find(
        (m) => m.run_id === run.id && m.role === "assistant"
      );
      const block = msg?.content?.find((b) => b.type === "text");
      setCurrentPrompt(
        block && "text" in block ? block.text.value : "Erreur lors de la génération"
      );
      setQuestionIndex((q) => q + 1);
      if (questionIndex + 1 > 30) setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const addPlayer = () => {
    const name = playerName.trim();
    if (name && !players.includes(name)) {
      setPlayers([...players, name]);
      setPlayerName("");
    }
  };

  const removePlayer = (name: string) => {
    setPlayers(players.filter((p) => p !== name));
  };

  return (
    <div className="container" style={{ position: "relative" }}>
      <button
        onClick={onQuit}
        disabled={loading}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "transparent",
          border: "none",
          fontSize: 20,
          cursor: "pointer",
          color: "var(--text-light)",
        }}
        title="Quitter le jeu"
      >
        ❌
      </button>

      {loading && <p className="loading">💬 GPT réfléchit…</p>}

      {step === 0 && (
        <GameRulesCard
          logo={stcmjscLogo}
          gameName="Si t’es chaud, moi je suis chaud"
          rules={[
            "▸Ajoutez vos prénoms (2 joueurs min.).",
            "▸Le jeu comporte 30 questions d'ambiance avec de l'alcool.",
            "▸Chaque question s’affiche individuellement et peut être de nature différente.",
            "Appuyez sur ‘Question suivante’ pour avancer dans le jeu.",
          ]}
          onNext={() => setStep(1)}
          loading={loading}
        />
      )}

      {step === 1 && (
        <>
          <p>Ajoute les prénoms des joueurs (au moins 2) :</p>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              className="fullWidth"
              placeholder="Prénom"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              disabled={loading}
            />
            <button onClick={addPlayer} disabled={loading || !playerName.trim()}>
              ➕
            </button>
          </div>
          <ul>
            {players.map((p) => (
              <li key={p}>
                {p}{" "}
                <button onClick={() => removePlayer(p)} disabled={loading}>
                  ❌
                </button>
              </li>
            ))}
          </ul>
          <button
            className={`fullWidth ${players.length < 2 || loading ? "disabledButton" : ""}`}
            onClick={startGame}
            disabled={players.length < 2 || loading}
          >
            🎲 Lancer la partie
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <p>
            <b>Question {Math.min(questionIndex, 30)} / 30</b>
          </p>
          {!loading && <p style={{ fontWeight: "bold" }}>{currentPrompt}</p>}
          <button
            className={`fullWidth ${loading ? "disabledButton" : ""}`}
            onClick={nextQuestion}
            disabled={loading}
          >
            Question suivante
          </button>
        </>
      )}

      {step === 3 && (
        <>
          <h3>🎉 Fin de la partie !</h3>
          <p>Bravo, vous avez survécu aux 30 questions ! 🍻</p>
          <button className="fullWidth" onClick={() => window.location.reload()}>
            Rejouer
          </button>
        </>
      )}
    </div>
  );
}
